import {
  BreakpointsConfiguration,
  breakpointToPixels,
  useBreakpointsConfiguration,
} from '@emico-react/breakpoints'
import React, { HTMLAttributes } from 'react'

import getImgProxyUrl, { ImgProxyParams } from './getImgProxyUrl'
import { Image, Props as ImageProps } from './Image'

export interface Size {
  breakpoint: number
  width: number
  height: number | undefined
  url: string
  params?: ImgProxyParams
}

export interface BreakpointImage {
  width: number
  height: number | undefined
  url: string
  params?: ImgProxyParams
}

type Breakpoints = { [key: string]: BreakpointImage }

interface Props extends Omit<ImageProps, 'url'> {
  /**
   * Picture breakpoints can be defined using devices (mobile, tablet,
   * desktop), breakpoints defined in layout.ts e.g. xs, sm, md, lg, xl
   * or using pixels. Using `{ '1500': ... }` as a breakpoint will add a
   * (min-width: 1500px) breakpoint.
   */
  breakpoints: Breakpoints

  /** A description of the Image, if the image is purely decorational with
   * no meaning this should be an empty string. */
  alt: string

  /**
   * Extra props to pass to picture. Normally you want to add props to
   * the image inside the picture.
   */
  pictureProps?: HTMLAttributes<HTMLPictureElement>
}

function getSizes(
  breakpointsConfiguration: BreakpointsConfiguration,
  breakpoints: Breakpoints,
) {
  // Get the pixel sizes for the breakpoints
  const sizes: Size[] = Object.entries(breakpoints).map(
    ([breakpoint, image]) => ({
      // It is min-width, so get the first, small breakpoint
      breakpoint: breakpointToPixels(breakpointsConfiguration, breakpoint)[0],
      width: image.width,
      height: image.height,
      url: image.url,
      params: image.params,
    }),
  )

  // Largest breakpoints must be first in order for the sources to work correctly.
  sizes.sort((a, b) => b.breakpoint - a.breakpoint)

  return sizes
}

export const Picture = ({
  breakpoints,
  alt,
  pictureProps,
  params: sharedParams,
  ...props
}: Props) => {
  const breakpointsConfiguration = useBreakpointsConfiguration()
  const sizes = getSizes(breakpointsConfiguration, breakpoints)
  const fallback = sizes[sizes.length - 1]

  // If fallback is not defined, there are no breakpoints defined
  if (!fallback) {
    throw new Error('At least one breakpoint should be defined.')
  }

  const sources = sizes
    .slice(0, -1) // The last and smallest breakpoint will be the fallback image.
    .map(({ breakpoint, width, height, url, params }) => {
      const media = `(min-width: ${breakpoint}px)`

      const standardSrc = getImgProxyUrl(url, {
        width,
        height,
        ...(params ?? sharedParams),
      })
      const x2Src = getImgProxyUrl(url, {
        width,
        height,
        dpr: 2,
        ...(params ?? sharedParams),
      })

      return (
        <source
          media={media}
          srcSet={`${standardSrc} ${width}w, ${x2Src} ${width * 2}w`}
          key={`${breakpoint}-${width}-${height}`}
        />
      )
    })

  return (
    <picture {...pictureProps}>
      {sources}
      <Image
        url={fallback.url}
        width={fallback.width}
        height={fallback.height ?? undefined}
        params={fallback.params ?? sharedParams}
        alt={alt}
        {...props}
      />
    </picture>
  )
}
